import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { PIPELINE_COUPLES_PAGE_SIZE, type PipelineCoupleList } from '@lib/constants/pipeline'
import type { PipelineCoupleDto, VenuePipelineDto } from '@lib/dto/pipeline.dto'
import { FUNNEL_TARGET_LABELS, type FunnelTarget } from '@lib/enums/funnel-stage.enum'
import { LOST_REASON_COUNT_LABELS } from '@lib/enums/lost-reason.enum'
import { dayInZone } from '@lib/helpers/ad-periods'
import type { PipelineCouplesQuery } from '@lib/input/pipeline.input'
import type { Instant } from '@lib/validators/temporal.validator'
import { Badge } from '@/components/ui/badge'
import { Pagination } from '@/components/ui/pagination'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ErrorState } from '@/components/ui/states'
import { Table, TableBody, TableCell, TableEmptyRow, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { requestErrorMessage } from '@/helpers/apiClient'
import { usePipelineCouples } from '@/hooks/usePipeline'
import { formatAdDay, formatCount } from '@/lib/adFormat'
import { serverPage } from '@/lib/pagination'

/** What a sheet was opened on: the couples behind a count, or one of the lists worth a look. */
export type PipelineOpened = { kind: 'count'; target: FunnelTarget } | { kind: 'list'; list: PipelineCoupleList }

interface CoupleSheetProps {
  venueId: string
  /** The venue's zone: whose days are shown. */
  timeZone: string
  /** The split, view and weeks the page shows. */
  query: Omit<PipelineCouplesQuery, 'target' | 'list' | 'page'>
  /** What was opened; null while nothing is. */
  opened: PipelineOpened | null
  /** The weeks shown, as the page says them. */
  periodLabel: string
  /** How many are in each list, for the tabs between the two kinds of couples who toured. */
  lookAt: VenuePipelineDto['lookAt']
  onClose: () => void
}

const DAY_MS = 24 * 60 * 60 * 1000

/** Each list's title, what it holds, and what its date column is. */
const LISTS: Readonly<Record<PipelineCoupleList, { title: string; holds: (period: string) => string; when: string }>> = {
  toured_deciding: { title: 'Toured, not booked yet', holds: (p) => `Couples who inquired ${p} and came to a tour or open house.`, when: 'Toured' },
  toured_lost: { title: 'Toured, not booked yet', holds: (p) => `Couples who inquired ${p} and came to a tour or open house.`, when: 'Toured' },
  toured_waiting: {
    title: 'Toured over 3 weeks ago, no decision yet',
    holds: () => 'Anyone who toured three weeks to six months ago and has neither booked nor been lost. Longest waiting first.',
    when: 'Toured',
  },
  holds_ending: { title: 'Holds that end in the next 7 days', holds: () => 'Their dates go back on the calendar after that. Soonest first.', when: 'Hold ends' },
  quiet_before_tour: {
    title: 'Stopped responding before a tour',
    holds: () => 'Lost to silence in the last 90 days without ever touring. Newest first.',
    when: 'Went quiet',
  },
}

function dayOf(at: Instant, timeZone: string): string {
  return formatAdDay(dayInZone(new Date(at), timeZone))
}

function daysAgo(at: Instant): string {
  const days = Math.floor((Date.now() - new Date(at).getTime()) / DAY_MS)
  return days === 1 ? '1 day ago' : `${formatCount(days)} days ago`
}

/** Their name, linked to their event, with where they came from and their GoHighLevel contacts under it. */
function CoupleCell({ couple }: { couple: PipelineCoupleDto }) {
  const name = couple.name ?? <span className="font-normal text-ink-muted">Name unavailable</span>
  return (
    <TableCell>
      {couple.event === null ? (
        <span className="font-medium text-ink">{name}</span>
      ) : (
        <Link to={`/events/${couple.event.id}`} aria-label={`Event: ${couple.event.name}`} className="font-medium text-ink hover:underline">
          {name}
        </Link>
      )}
      <span className="flex flex-wrap gap-x-1.5 text-small text-ink-muted">
        {couple.source === null ? null : <span>{couple.source}</span>}
        {couple.leads.map((lead) => (
          <a key={lead.url} href={lead.url} target="_blank" rel="noreferrer" className="text-accent-ink hover:underline">
            {lead.locationName}
          </a>
        ))}
      </span>
    </TableCell>
  )
}

/** Where a couple is: lost and why, else what they've had along the way. */
function WhereThey({ couple }: { couple: PipelineCoupleDto }) {
  if (couple.lostReason !== null) return <span>Lost: {LOST_REASON_COUNT_LABELS[couple.lostReason]}</span>
  const parts: ReactNode[] = []
  if (couple.proposalSent === false) parts.push(<Badge key="proposal" tone="warning">No proposal yet</Badge>)
  else parts.push(<span key="now">{couple.proposalSent === true ? 'Proposal sent' : FUNNEL_TARGET_LABELS[couple.now]}</span>)
  if (couple.holdUntil !== null) parts.push(<span key="hold" className="block text-small text-ink-muted">Date on hold until {formatAdDay(couple.holdUntil)}</span>)
  return <>{parts}</>
}

/** The date a list orders by: when they toured, when their hold ends, when they went quiet. */
function WhenCell({ couple, list, timeZone }: { couple: PipelineCoupleDto; list: PipelineCoupleList; timeZone: string }) {
  if (list === 'holds_ending') return <TableCell className="whitespace-nowrap">{couple.holdUntil === null ? '—' : formatAdDay(couple.holdUntil)}</TableCell>
  const at = list === 'quiet_before_tour' ? couple.reachedAt : couple.touredAt
  if (at === null) return <TableCell className="text-ink-muted">Before records began</TableCell>
  return (
    <TableCell className="whitespace-nowrap">
      {dayOf(at, timeZone)}
      <span className="block font-mono text-small text-ink-muted tabular-nums">{daysAgo(at)}</span>
    </TableCell>
  )
}

function LoadingRows({ columns }: { columns: number }) {
  return Array.from({ length: 3 }, (_, i) => (
    <TableRow key={i}>
      <TableCell colSpan={columns}>
        <div className="h-4 w-2/3 animate-pulse rounded-sm bg-surface-sunken" />
      </TableCell>
    </TableRow>
  ))
}

function CountRows({ couples, timeZone }: { couples: PipelineCoupleDto[]; timeZone: string }) {
  return couples.map((couple, i) => (
    <TableRow key={`${couple.event?.id ?? couple.leads[0]?.url ?? ''}:${String(i)}`}>
      <CoupleCell couple={couple} />
      <TableCell className="whitespace-nowrap text-ink-muted">{couple.reachedAt === null ? 'Before records began' : dayOf(couple.reachedAt, timeZone)}</TableCell>
      <TableCell className="whitespace-nowrap">{FUNNEL_TARGET_LABELS[couple.now]}</TableCell>
    </TableRow>
  ))
}

function ListRows({ couples, list, timeZone }: { couples: PipelineCoupleDto[]; list: PipelineCoupleList; timeZone: string }) {
  return couples.map((couple, i) => (
    <TableRow key={`${couple.event?.id ?? couple.leads[0]?.url ?? ''}:${String(i)}`}>
      <CoupleCell couple={couple} />
      <WhenCell couple={couple} list={list} timeZone={timeZone} />
      <TableCell>
        <WhereThey couple={couple} />
      </TableCell>
    </TableRow>
  ))
}

type TouredTab = 'undecided' | 'lost'

/** The list a sheet shows: the one opened, or, for the couples who toured, the tab chosen since. */
function listShown(opened: PipelineOpened | null, tab: TouredTab | null): PipelineCoupleList | null {
  if (opened?.kind !== 'list') return null
  const toured = opened.list === 'toured_deciding' || opened.list === 'toured_lost'
  if (!toured || tab === null) return opened.list
  return tab === 'lost' ? 'toured_lost' : 'toured_deciding'
}

/** "Lost 20", or just "Lost" where the count isn't known. */
function tabLabel(label: string, count: number | null | undefined): string {
  return count == null ? label : `${label} ${formatCount(count)}`
}

/**
 * The couples behind one of a venue's counts, or in one of its lists worth a
 * look (docs/pipeline-view.spec.md, "The couple list", "Couples to look at"),
 * 25 at a time, each with where they came from and what's known of their
 * decision. The two kinds of couples who toured and haven't booked, undecided
 * and lost, are tabs of one sheet.
 */
export function CoupleSheet({ venueId, timeZone, query, opened, periodLabel, lookAt, onClose }: CoupleSheetProps) {
  const [page, setPage] = useState(1)
  // Null: the tab the sheet was opened on.
  const [tab, setTab] = useState<TouredTab | null>(null)
  const list = listShown(opened, tab)
  const asked: PipelineCouplesQuery =
    opened?.kind === 'count' ? { ...query, target: opened.target, page } : { ...query, list: list ?? 'toured_deciding', page }
  const couplesQuery = usePipelineCouples(venueId, asked, opened !== null)
  const data = couplesQuery.data
  const couples = data?.couples ?? []
  const paging = serverPage(page, data?.total ?? 0, couples.length, PIPELINE_COUPLES_PAGE_SIZE)
  const toured = list === 'toured_deciding' || list === 'toured_lost'

  function close(open: boolean): void {
    if (open) return
    setPage(1)
    setTab(null)
    onClose()
  }

  function title(): string {
    if (opened === null) return ''
    if (opened.kind === 'count') return `${FUNNEL_TARGET_LABELS[opened.target]} · ${periodLabel}`
    return LISTS[opened.list].title
  }

  function description(): string {
    if (opened?.kind === 'list') return LISTS[opened.list].holds(periodLabel)
    return query.view === 'activity' ? 'Couples who reached it in these weeks.' : 'Couples who inquired in these weeks and reached it.'
  }

  return (
    <Sheet open={opened !== null} onOpenChange={close}>
      <SheetContent side="right">
        <SheetHeader>
          <SheetTitle>{title()}</SheetTitle>
          <SheetDescription>{description()}</SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          {toured ? (
            <SegmentedControl
              label="Show"
              size="sm"
              value={list === 'toured_lost' ? 'lost' : 'undecided'}
              options={[
                { value: 'undecided', label: tabLabel('Undecided', lookAt?.touredDeciding) },
                { value: 'lost', label: tabLabel('Lost', lookAt?.touredLost) },
              ]}
              onValueChange={(v) => {
                setTab(v)
                setPage(1)
              }}
            />
          ) : null}
          {couplesQuery.isError ? (
            <ErrorState message={requestErrorMessage(couplesQuery.error)} onRetry={() => void couplesQuery.refetch()} />
          ) : (
            <Table
              heading={data === undefined ? null : `${formatCount(data.total)} ${data.total === 1 ? 'couple' : 'couples'}`}
              footer={<Pagination {...paging} unit="couples" onPage={setPage} />}
            >
              <TableHeader>
                <TableHead>Couple</TableHead>
                <TableHead>{list === null ? 'Reached' : LISTS[list].when}</TableHead>
                <TableHead>{list === null ? 'Now' : 'Where they are'}</TableHead>
              </TableHeader>
              <TableBody>
                {couplesQuery.isPending ? <LoadingRows columns={3} /> : null}
                {!couplesQuery.isPending && couples.length === 0 ? <TableEmptyRow colSpan={3}>No couples.</TableEmptyRow> : null}
                {couplesQuery.isPending || list === null ? null : <ListRows couples={couples} list={list} timeZone={timeZone} />}
                {couplesQuery.isPending || list !== null ? null : <CountRows couples={couples} timeZone={timeZone} />}
              </TableBody>
            </Table>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
